/**
 * Where the operator is. PURE — no DOM, no network, no clock.
 *
 * THE GUIDED FLOW IS THE APP, as of 2026-09-21. It was reached from a "v2 test" link
 * while it was being judged; it has been, and the flat robot table it replaced is gone.
 *
 * The vendor-query panel survives — it is the only way to invoke an arbitrary registry
 * call — but as a tool ON the site screen rather than a separate destination. `fleet`
 * therefore no longer exists as a view.
 *
 * SCENE AND FLOOR ARE NOT ROUTES. They began as `scenes -> scene` screens and became
 * dropdowns on the site: picking a scene FILTERS what is already on screen rather than
 * being a journey somewhere else, and the robot list has to stay visible while it
 * happens — watching robots move on that map is the entire purpose. A path would have
 * hidden them behind the map.
 *
 * IT STARTS AT SITES, NOT STORES. A site is OUR tenancy boundary: it scopes the broker
 * ACL, it keys every map in this app, and the token names every one of them whether or
 * not that site has published anything yet. A store is the VENDOR's concept and learning
 * the list costs a vendor call per site. Starting from stores also meant picking ONE
 * site to ask, which silently hid every other — the bug this shape replaced.
 *
 * That is the inverse of how this was planned, and on purpose: the guided flow is the
 * one being judged, so it has to be somewhere you go to look at it rather than the thing
 * that greets you when you open the app to do something else. Making it the landing
 * screen is a one-line change to HOME once it has earned it.
 *
 * Which screen you are on is derived from which fields are set, so an impossible state
 * (a robot with no store) cannot be represented by accident.
 *
 * STATE LIVES IN THE URL HASH, not in localStorage. It is NAVIGATION, not data:
 *
 *   * a link to a robot is shareable, which is how one person shows another what they
 *     are looking at;
 *   * it survives reload;
 *   * `hashchange` gives back and forward for free, and the browser's own buttons are
 *     the ones people reach for;
 *   * persisted navigation would resume somebody into a store their grant no longer
 *     covers, and the first thing they would see is an empty screen rather than a
 *     refusal they could understand.
 *
 * EVERY STATE CARRIES A `source`, even though there is exactly one today. It is the
 * broker a screen is reading. This is not speculative generality — it is load-bearing
 * the moment a second source exists, because the answer cache is per-broker: a cached
 * answer is scoped to the broker that holds it, so "which source" and "whose cache" are
 * the same question. Adding it later means revisiting the hash format and every screen
 * at once, which is exactly the retrofit this avoids. See CR 006.
 */

export type View = "sites" | "robots" | "robot";

/** The cloud broker. A LAN broker via the relay would be a second one. */
export const DEFAULT_SOURCE = "cloud";

export interface Nav {
  source: string;
  view: View;
  siteId?: string;
  robotId?: string;
}

/** Where an empty or unreadable hash lands. */
export const HOME: Nav = { source: DEFAULT_SOURCE, view: "sites" };

/**
 * A site id is not a store id, though today they are the same string.
 *
 * `normalize_fleet_list` in the adapter sets `site_id = store_id`, so identity is correct
 * right now. It will not always be: a site is OUR tenancy boundary (it scopes the broker
 * ACL) while a store is the VENDOR's, and one site could carry two vendors' stores. Every
 * conversion goes through here so that day is one function, not a search.
 *
 * The app is keyed by SITE throughout — the fleet map, the manifests, the answer cache —
 * so this is only needed where a vendor call wants a `siteId` parameter.
 */
export function storeIdFor(siteId: string): string {
  return siteId;
}

// ---- the hash ----------------------------------------------------------------
//
// Explicit markers (`store/`, `robot/`, `scene/`) rather than bare positional segments.
// A bare `#/cloud/C007` cannot be told from `#/cloud/stores` without knowing every store
// id, and a store legitimately named "fleet" would route to the wrong screen. It is a
// URL: legible beats short.

function enc(s: string): string {
  return encodeURIComponent(s);
}

export function formatHash(nav: Nav): string {
  const s = enc(nav.source);
  switch (nav.view) {
    case "sites":
      return `#/${s}/sites`;
    case "robots":
      return `#/${s}/site/${enc(nav.siteId ?? "")}`;
    case "robot":
      return `#/${s}/site/${enc(nav.siteId ?? "")}/robot/${enc(nav.robotId ?? "")}`;
    default:
      return `#/${s}/sites`;
  }
}

/**
 * Read a hash. NEVER THROWS and never returns null: a hash is user-editable text and
 * arrives from the address bar, a stale bookmark, or a link someone pasted into chat.
 * Anything unrecognised lands on `stores`, which is the one screen that always works.
 */
export function parseHash(hash: string): Nav {
  const raw = (hash || "").replace(/^#\/?/, "");
  const parts = raw.split("/").filter(Boolean).map((p) => {
    try {
      return decodeURIComponent(p);
    } catch {
      return p;            // a lone `%` is not a reason to lose the whole route
    }
  });
  if (!parts.length) return HOME;

  const source = parts[0] || DEFAULT_SOURCE;
  const rest = parts.slice(1);

  if (rest[0] === "sites") return { source, view: "sites" };
  if (rest[0] === "site" && rest[1]) {
    const siteId = rest[1];
    if (rest[2] === "robot" && rest[3]) {
      return { source, view: "robot", siteId, robotId: rest[3] };
    }
    return { source, view: "robots", siteId };
  }
  // Anything else — a stale bookmark from the old flat-table view, a typo, `#/cloud` on
  // its own — lands on the site list, which is the one screen that always works.
  return { source, view: "sites" };
}

// ---- moving around -----------------------------------------------------------

/**
 * One step up. `back` is a real transition rather than `history.back()`, because the
 * browser's history may have come from somewhere else entirely — a link straight to a
 * robot has no parent in it, and "back" would leave the app.
 */
export function parent(nav: Nav): Nav {
  switch (nav.view) {
    case "robot":
      return { source: nav.source, view: "robots", siteId: nav.siteId };
    case "robots":
      return { source: nav.source, view: "sites" };
    case "sites":
    default:
      return { source: nav.source, view: "sites" };
  }
}

export interface Crumb { label: string; nav: Nav }

/**
 * The trail, including the current screen (last, and the caller renders it inert).
 *
 * The SOURCE is the first crumb and is always shown, even with one broker. "Which broker
 * am I looking at" is precisely the question a surprising number provokes, and a reading
 * from the wrong site looks exactly like a reading from this one.
 */
export function breadcrumb(nav: Nav, siteLabel?: (id: string) => string): Crumb[] {
  const label = (id: string) => siteLabel?.(id) ?? id;
  // The SOURCE first, always — "which broker am I looking at" is exactly the question a
  // surprising number provokes, and a reading from the wrong site looks like a reading
  // from this one. It is also the way back to the site list from anywhere.
  const out: Crumb[] = [
    { label: nav.source, nav: { source: nav.source, view: "sites" } },
  ];
  if (nav.view === "sites") return out;

  const siteId = nav.siteId ?? "";
  out.push({ label: label(siteId), nav: { source: nav.source, view: "robots", siteId } });
  if (nav.view === "robot") out.push({ label: nav.robotId ?? "", nav });
  return out;
}

/**
 * Skip a choice of one.
 *
 * With a single site, "pick your site" is a screen that asks a question with one answer
 * — so it advances to that site's robots. The breadcrumb still names the site, so the
 * step is visible rather than hidden, and going back up still shows the list.
 *
 * Only from `sites`, and only on exactly one: from anywhere else this would fight the
 * operator's own navigation, and with zero sites there is nothing to advance to (that
 * screen has something to say — "your account has no grants" — which is not a blank).
 */
export function autoAdvance(nav: Nav, siteIds: string[]): Nav {
  if (nav.view !== "sites" || siteIds.length !== 1) return nav;
  return { source: nav.source, view: "robots", siteId: siteIds[0] };
}
